import React, { useEffect, useState } from 'react';
import Box from '@nubank/nuds-web/components/Box/Box';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import { Grid } from '@nubank/nuds-web/components/Grid/Grid';
import styled, { css, keyframes } from 'styled-components';
import Image from '@nubank/nuds-web/components/Image/Image';
import Button from '@nubank/nuds-web/components/Button/Button';
import { breakpointsMedia } from '@nubank/nuds-web/styles/breakpoints';

import useFigPiiExperiment from '@nubank/www-latam-commons/utils/figpii/useFigPiiExperiment';
import { getInviter } from '@nubank/www-latam-commons/services/prospect';
import { queryStringToParameters } from '@nubank/www-latam-commons/utils/urlUtils';
import { getPersistedMGMID } from '@nubank/www-latam-commons/utils/mgmUtils';
import ImagePreload from '@nubank/www-latam-commons/components/ImagePreload/ImagePreload';
import useWindowSize from '@nubank/www-latam-commons/utils/hooks/useWindowSize';

import { MGM_CTA_EXPERIMENT_ID, MGM_CTA_EXPERIMENT_VARIANT, MGM_CTA_PURPLE_EXPERIMENT_VARIANT } from '../../../../utils/experiments';
import { ERROR_SEVERITY, sentryException } from '../../../../utils/sentry';
import FormBoxMGM from '../../../../components/FormBox/FormBoxMGM';
import FormBox from '../../../../components/FormBox/FormBox';

const HERO_IMAGE = {
  xs: 'mgm/hero-lite/hero.svg',
};

const StyledTypography = styled(Typography)`
  background: ${props => (props.isCTAPurpleActive ? 'white' : 'linear-gradient(99.92deg, #9F2DEB 18.57%, #6200A3 71.77%)')};
  /* stylelint-disable property-no-vendor-prefix, property-no-unknown */
  -webkit-text-fill-color: transparent;
  -webkit-background-clip: text;
  background-clip: text;

  ${breakpointsMedia({
    xs: css`
      font-size: 30px;
      font-weight: 600;
    `,
    md: css`
      font-size: 45px;
    `,
    lg: css`
      font-size: 50px;
    `,
    xl: css`
      font-size: 60px;
    `,
  })}

`;

const InviterTypography = styled(Typography)`
  background: ${props => (props.isCTAPurpleActive ? 'white' : 'linear-gradient(99.92deg, #9F2DEB 18.57%, #6200A3 71.77%)')};
  -webkit-text-fill-color: transparent;
  -webkit-background-clip: text;
  background-clip: text;
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30%);
  }
  to {
    opacity: 1;
    transform: translateY(0%);
  }
`;

const StyledBox = styled(Box)`
  animation: ${fadeIn} .6s linear;

  ${breakpointsMedia({
    lg: css`
      animation: none;
    `,
  })}
`;

const Sparkles = styled(Image)`
  margin-left: -5px;
  margin-top: -3px;
`;

const StyledImage = styled(Image)`
  min-height: 150px;
  width: auto;
  max-width: 100%;
  
  ${breakpointsMedia({
    xs: css`
      max-height: 335px;
    `,
    md: css`
      max-height: 500px;
    `,
    lg: css`
      max-height: 600px;
      margin-top: ${props => (props.purpleBg ? '20px' : '15px')};
    `,
  })}
`;

const BackgroundBox = styled(Box)`
  background: ${props => (props.purpleBg ? 'linear-gradient(99.92deg, #9F2DEB 18.57%, #6200A3 71.77%)' : 'transparent')};
  min-height: ${props => (props.purpleBg ? '100vh' : 'auto')};

  @media (height >= 1200px) {
    min-height: ${props => (props.purpleBg ? '1200px' : 'auto')};
  }
`;

const HeroLite = () => {
  const [inviterName, setInviterName] = useState('');
  const { height } = useWindowSize();
  const [screenHeight, setScreenHeight] = useState(0);

  // CTA Experiment
  const ctaExperimentVariant = useFigPiiExperiment(MGM_CTA_EXPERIMENT_ID);

  const isCTADefaultActive = ctaExperimentVariant === MGM_CTA_EXPERIMENT_VARIANT;
  const isCTAPurpleActive = ctaExperimentVariant === MGM_CTA_PURPLE_EXPERIMENT_VARIANT;
  const showNewCTA = isCTADefaultActive || isCTAPurpleActive;

  useEffect(() => {
    setScreenHeight(window.innerHeight);
  }, []);

  const getUserInfo = async () => {
    const queryStringObj = queryStringToParameters(document.location.search);
    const inviteId = queryStringObj.id || getPersistedMGMID();

    if (inviteId) {
      try {
        const { customer } = await getInviter(inviteId);

        setInviterName(customer.name);
      } catch (error) {
        sentryException({
          error,
          flow: 'mgm page',
          checkpoint: 'getInviter() function',
          namespace: '<MGM />',
          level: ERROR_SEVERITY.DEFAULT,
        });
      }
    }
  };

  useEffect(() => {
    // Add a delay to avoid "Load failed" error in iOS 18
    // when document becomes visible after being minimized
    const timer = setTimeout(() => {
      getUserInfo();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const scrollDown = () => {
    window.scrollTo({
      top: height - 80,
      behavior: 'smooth',
    });
  };

  const userName = inviterName ? inviterName.split(' ')[0] : '';
  const addPixels = userName ? 0 : 50;

  return (
    <>
      <ImagePreload srcSet={HERO_IMAGE} />
      <BackgroundBox
        display="flex"
        justifyContent="center"
        paddingTop={{ xs: '0x', lg: isCTAPurpleActive ? '24x' : '8x' }}
        marginBottom={{ xs: '8x', lg: '8x' }}
        purpleBg={isCTAPurpleActive}
      >
        <StyledBox
          tag="section"
          paddingTop={{ xs: '80px', lg: '100px' }}
          paddingHorizontal={{ xs: '6x', md: '48px', lg: '56px' }}
          height="100%"
          width="100%"
          maxWidth="1800px"
        >
          <Grid
            paddingRight="0"
            paddingLeft="0"
            gridTemplateColumns={{ xs: 'auto', lg: 'repeat(11,minmax(0,1fr))' }}
          >

            {/* TITLE */}

            <Grid.Child
              gridRow={{ xs: '1', md: '1', lg: '1' }}
              gridColumn={{ xs: '1 / span 4', md: ' 1 / span 6', lg: '2 / span 6' }}
              justifySelf={{ xs: 'center', lg: 'flex-start' }}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems={{ xs: 'center', lg: 'flex-start' }}
                justifyContent={{ xs: 'center', lg: 'flex-start' }}
                marginBottom={{ xs: '6x', md: '12x', lg: '0x' }}
              >
                <StyledTypography
                  intlKey="MGM.HERO_LITE.TITLE"
                  textAlign="center"
                  width={{ xs: '14ch', md: '100%' }}
                  marginBottom={{ xs: '2x', lg: '3x', xl: '4x' }}
                  isCTAPurpleActive={isCTAPurpleActive}
                />
                <Box
                  background={
                    isCTAPurpleActive ? 'linear-gradient(99.92deg, #6200A3 18.57%, #9F2DEB 109.77%)'
                      : 'linear-gradient(99.92deg, #9F2DEB 18.57%, #6200A3 71.77%)'
                  }
                  borderRadius={{ xs: '30px', lg: '50px' }}
                  paddingVertical={{ xs: '2x', lg: '4x' }}
                  paddingLeft={{ xs: '6x', lg: '8x' }}
                  paddingRight={{ xs: '2x', lg: '4x' }}
                  display="flex"
                  flexDirection="row"
                >
                  <Typography
                    intlKey="MGM.HERO_LITE.TITLE.2"
                    variant="heading1"
                    color="white"
                    textAlign="center"
                  />

                  <Sparkles
                    src="mgm/hero-lite/sparkles.svg"
                    alt="Brillo"
                    width="35px"
                    webP
                  />
                </Box>
              </Box>
            </Grid.Child>

            {/* IMAGE */}

            <Grid.Child
              gridRow={{ xs: '2', md: '2', lg: '1' }}
              gridColumn={{ xs: '1 / span 4', md: ' 1 / span 6', lg: '7 / span 10' }}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems={{ xs: 'center' }}
                justifyContent={{ xs: 'center' }}
              >
                <StyledImage
                  srcSet={HERO_IMAGE}
                  alt="Ilustración de mujer con una tarjeta Nu"
                  height={{
                    xs: `${screenHeight - (550 - addPixels)}px`,
                    md: `${screenHeight - (690 - addPixels)}px`,
                    lg: '75%',
                  }}
                  webP
                  purpleBg={isCTAPurpleActive}
                />
              </Box>
            </Grid.Child>

            {/* FORM */}
            <Grid.Child
              gridRow={{ xs: '3', md: '3', lg: '1' }}
              gridColumn={{ xs: '1 / span 4', md: ' 1 / span 6', lg: '2 / span 6' }}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems={{ xs: 'center', lg: 'flex-start' }}
                justifyContent={{ xs: 'center', lg: 'flex-start' }}
                marginTop={{ xs: showNewCTA ? '3x' : '6x', md: '12x', lg: '250px' }}
              >
                {
                userName ? (
                  <InviterTypography
                    variant="heading3"
                    marginBottom={{ xs: showNewCTA ? '3x' : '4x', md: '6x', lg: '8x' }}
                    isCTAPurpleActive={isCTAPurpleActive}
                  >
                    ¡
                    {userName}
                    {' '}
                    te invitó a Nu!
                  </InviterTypography>
                ) : null
              }

                <Typography
                  variant="paragraph2"
                  intlKey="MGM.HERO_LITE.DESCRIPTION"
                  intlValues={{
                    strong: props => (
                      <Typography tag="span" strong variant="paragraph2">
                        {props }
                      </Typography>
                    ),
                  }}
                  textAlign={{ xs: 'center', lg: 'left' }}
                  maxWidth={{ xs: '100%', md: '320px', lg: '400px' }}
                  marginBottom={{ xs: '2x', md: '4x' }}
                  color={isCTAPurpleActive ? 'white' : 'black'}
                />
                { showNewCTA ? (
                  <FormBox
                    ctaHide
                    buttonLabel="HOME.HERO.BUTTON_LABEL.MULTIPRODUCT"
                    inputAndButtonWidth={{ xs: '300px' }}
                    inputBoxShadow="0 0 29px rgb(130 10 209 / 10%)"
                    inputLabelColor="#820AD1"
                    errorMessageColor="#D01D1C"
                    boxShadow={!isCTAPurpleActive}
                  />
                ) : (
                  <FormBoxMGM
                    ctaHide
                    buttonLabel="HOME.HERO.BUTTON_LABEL.MULTIPRODUCT"
                    inputAndButtonWidth={{ xs: '300px' }}
                    inputBoxShadow="0 0 29px rgb(130 10 209 / 10%)"
                    inputLabelColor="#820AD1"
                    errorMessageColor="#D01D1C"
                  />
                )}

              </Box>
            </Grid.Child>

            {/* ARROW DOWN BTN */}

            <Grid.Child
              gridRow={{ xs: '4', md: '4', lg: '2' }}
              gridColumn={{ xs: '1 / span 4', md: ' 1 / span 6', lg: '2 / span 10' }}
              alignSelf={{ xs: 'end' }}
              justifySelf={{ xs: 'center' }}
            >
              <Box
                marginTop={{ xs: '0x', md: '4x', lg: '12x' }}
                marginBottom={{ xs: '8x', md: '18x', lg: '80px' }}
              >
                { !showNewCTA && (
                <Button
                  variant="contained"
                  iconProps={{ name: 'arrow-down', title: 'Bajar a siguiente sección' }}
                  onClick={scrollDown}
                  styleVariant={isCTAPurpleActive ? 'white' : 'primary'}
                />
                )}
              </Box>

            </Grid.Child>

          </Grid>

        </StyledBox>
      </BackgroundBox>
    </>
  );
};

export default HeroLite;
